import React, { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import Layout from "components/Layout";
import EmphasisText from "components/EmphasisText";
import LeonardoLanguageToggle from "components/LeonardoLanguageToggle";
import api from "utils/api";
import { buildTawanHandoffReturnUrl } from "utils/authHandoff";
import { hasTawanReferrer } from "utils/referrerCookie";
import { getTawanSpaPublicUrl } from "utils/siteUrls";
import { resolvePostLoginPath } from "utils/adminLoginRedirect";
import { buildAuthHref } from "utils/registration";
import { useLeonardoLocale } from "scenes/leonardoNikolai/LeonardoLocaleContext";
import { setLogin } from "state";
import "styles/auth.css";

const LoginPage = () => {
  const [form, setForm] = useState({ email: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { shellT, isLeonardoFlow } = useLeonardoLocale();
  const params = new URLSearchParams(location.search);
  const next = params.get("next") || "/konto";

  const showTawanSignIn = useMemo(
    () =>
      hasTawanReferrer() ||
      new URLSearchParams(location.search).get("from") === "tawan-spa",
    [location.search]
  );

  const tawanLoginUrl = useMemo(() => {
    const returnTo = buildTawanHandoffReturnUrl(next);
    return `${getTawanSpaPublicUrl()}/einloggen?return_to=${encodeURIComponent(returnTo)}`;
  }, [next]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post("/auth/login", form);
      dispatch(setLogin({ token: data.token, user: data.user }));
      navigate(resolvePostLoginPath(data.user, next), { replace: true });
    } catch (err) {
      setError(err?.response?.data?.message || shellT("shell.auth.login.failed"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout>
      <section className="auth-page">
        <div className="auth-card">
          {isLeonardoFlow && (
            <div className="auth-lang-toggle-row">
              <LeonardoLanguageToggle />
            </div>
          )}
          <div className="auth-kicker">{shellT("shell.auth.login.kicker")}</div>
          <h1>
            <EmphasisText text={shellT("shell.auth.login.title")} />
          </h1>
          <p className="auth-lead">{shellT("shell.auth.login.lead")}</p>

          {showTawanSignIn && (
            <div className="auth-tawan-panel">
              <p>
                <strong>Tawan Spa Kunde?</strong> Du kannst dich mit dem gleichen
                Konto anmelden — E-Mail und Passwort sind für beide Seiten gültig.
              </p>
              <a
                href={tawanLoginUrl}
                className="auth-tawan-btn"
              >
                Mit Tawan Spa anmelden →
              </a>
            </div>
          )}

          {error && <div className="auth-alert is-error">{error}</div>}

          <form className="auth-form" onSubmit={handleSubmit} noValidate>
            <div>
              <label htmlFor="login-email">{shellT("shell.auth.login.email")}</label>
              <input
                id="login-email"
                type="email"
                autoComplete="email"
                required
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div>
              <label htmlFor="login-password">{shellT("shell.auth.login.password")}</label>
              <input
                id="login-password"
                type="password"
                autoComplete="current-password"
                required
                value={form.password}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
            <div className="auth-actions">
              <button type="submit" className="auth-submit" disabled={submitting}>
                {submitting
                  ? shellT("shell.auth.login.submitting")
                  : shellT("shell.auth.login.submit")}
              </button>
            </div>
          </form>

          <div className="auth-links">
            <span>
              {shellT("shell.auth.login.noAccount")}{" "}
              <Link to={buildAuthHref("/registrieren", next !== "/konto" ? { next } : {})}>
                {shellT("shell.auth.login.registerLink")}
              </Link>
            </span>
            <Link to="/auth/passwort-vergessen">{shellT("shell.auth.login.forgotPassword")}</Link>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default LoginPage;
